/**
 * DailyBlast-derived indicator math, ported from JSR_DailyBlast.txt.
 *
 * Pure functions — no React / chart deps. Consumers call these once per
 * candles-array change and feed the outputs into the chart layer.
 */

import { HTF_LADDER } from './intervalCatalog';

export interface CandleRow {
  time: number;      // IST-shifted unix seconds
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

/* ────── basic stats ────── */

export function sma(src: Array<number | null>, period: number): Array<number | null> {
  const out: Array<number | null> = new Array(src.length).fill(null);
  let sum = 0, cnt = 0;
  const buf: number[] = [];
  for (let i = 0; i < src.length; i++) {
    const v = src[i];
    if (v == null) { buf.push(NaN); continue; }
    buf.push(v); sum += v; cnt++;
    // Round 70 — `while`, not `if`. Previous code only shifted one entry per
    // valid push, so when `src` had a long run of leading nulls (typical when
    // the source itself is the output of EMA(200)+SMA+kernel and starts being
    // valid only after ~212 bars), the buf stayed inflated and cnt grew
    // linearly with valid pushes. The output ended up as
    //   (cnt × mean) / period   instead of   mean
    // i.e. the SMA was off by a factor of cnt/period — measurably ~10×
    // for CMid on a typical chart, which pushed the dashed line into
    // millions and squeezed the candles to the bottom of the price scale.
    while (buf.length > period) {
      const old = buf.shift();
      if (old != null && !Number.isNaN(old)) { sum -= old; cnt--; }
    }
    if (cnt >= period) out[i] = sum / period;
  }
  return out;
}

export function stdDev(src: Array<number | null>, mean: Array<number | null>, period: number): Array<number | null> {
  const out: Array<number | null> = new Array(src.length).fill(null);
  for (let i = period - 1; i < src.length; i++) {
    const m = mean[i];
    if (m == null) continue;
    let s = 0, n = 0;
    for (let k = i - period + 1; k <= i; k++) {
      const v = src[k];
      if (v == null) continue;
      s += (v - m) * (v - m); n++;
    }
    if (n >= period) out[i] = Math.sqrt(s / period);
  }
  return out;
}

export function ema(src: Array<number | null>, period: number): Array<number | null> {
  const out: Array<number | null> = new Array(src.length).fill(null);
  const k = 2 / (period + 1);
  let prev: number | null = null;
  // seed with first SMA
  let sum = 0, cnt = 0;
  for (let i = 0; i < src.length; i++) {
    const v = src[i];
    if (prev == null) {
      if (v == null) continue;
      sum += v; cnt++;
      if (cnt === period) { prev = sum / period; out[i] = prev; }
    } else {
      if (v == null) { out[i] = prev; continue; }
      prev = v * k + prev * (1 - k);
      out[i] = prev;
    }
  }
  return out;
}

/**
 * Pine-faithful EMA — matches TradingView's `ta.ema`, which seeds with the
 * FIRST available source value and recurses from there, yielding a value on
 * every bar from the first valid one (just biased toward the seed early, then
 * converging). The SMA-seeded `ema()` above instead returns null for the first
 * `period` bars.
 *
 * R108 — the AvgCloud's Combined Midline blends EMA(200) at CTF (4H) and HTF
 * (Daily) cadence. On symbols with limited history (fewer than ~200 daily
 * bars) the SMA-seeded EMA(200) is entirely null, so the CTF band's strict-null
 * voids it and the Combined Envelope collapses to the fast LTF-only band — the
 * midline then hugs price and the cloud is thin and flips constantly. Pine has
 * an EMA value from the first bar, so its slow CTF/HTF band always contributes
 * → thick, stable cloud. Use this for the envelope EMAs to match TradingView.
 */
export function emaSeeded(src: Array<number | null>, period: number): Array<number | null> {
  const out: Array<number | null> = new Array(src.length).fill(null);
  const k = 2 / (period + 1);
  let prev: number | null = null;
  for (let i = 0; i < src.length; i++) {
    const v = src[i];
    if (v == null || !Number.isFinite(v)) { out[i] = prev; continue; }
    prev = prev == null ? v : v * k + prev * (1 - k);
    out[i] = prev;
  }
  return out;
}

/* ────── linear regression (rolling window, last-bar value) ────── */
export function linreg(src: Array<number | null>, period: number): Array<number | null> {
  const out: Array<number | null> = new Array(src.length).fill(null);
  for (let i = period - 1; i < src.length; i++) {
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0, n = 0;
    for (let k = 0; k < period; k++) {
      const v = src[i - period + 1 + k];
      if (v == null) { n = 0; break; }
      const x = k;
      sumX += x; sumY += v; sumXY += x * v; sumX2 += x * x; n++;
    }
    if (n === period) {
      const denom = n * sumX2 - sumX * sumX;
      if (denom !== 0) {
        const slope = (n * sumXY - sumX * sumY) / denom;
        const intercept = (sumY - slope * sumX) / n;
        out[i] = slope * (period - 1) + intercept;
      }
    }
  }
  return out;
}

/* ────── Rational Quadratic Kernel (Pine v5 RQ kernel, end-point value) ──────
   h (lookback), r (relative weighting, 8.0 default), startBar (regression start).

   R109 — match jdehorty/KernelFunctions/2 `rationalQuadratic` EXACTLY (the lib
   the JSR pinescript imports). It sums `i = 0 .. startAtBar+1` (startAtBar+2
   terms; 27 for startBar=25) with weight
       w = (1 + i² / (lookback² · 2 · relativeWeight))^(-relativeWeight).
   The previous port summed only `h+1` (9) terms AND used denominator `h·2·r`
   (a missing square → 128 instead of 1024). Together that made the kernel
   ~5-bar fast instead of Pine's ~20-bar smooth. Since the kernel carries 2/3
   of the avg_line weight, the Combined Midline tracked price far too closely,
   so the AvgCloud was thin and flipped colour constantly vs TradingView's
   thick, stable cloud. (The backend rockBandMath kernel already used the
   correct denominator — this brings the chart in line.) */
export function rqKernel(src: Array<number | null>, h: number, r: number, startBar: number): Array<number | null> {
  const out: Array<number | null> = new Array(src.length).fill(null);
  const kmax = startBar + 1;          // Pine loops i = 0 .. startAtBar+1 (inclusive)
  const denom = h * h * 2 * r;        // lookback² · 2 · relativeWeight
  for (let i = kmax; i < src.length; i++) {
    let num = 0, den = 0;
    let bad = false;
    for (let k = 0; k <= kmax; k++) {
      const v = src[i - k];
      if (v == null) { bad = true; break; }
      const w = Math.pow(1 + (k * k) / denom, -r);
      num += v * w; den += w;
    }
    if (!bad && den > 0) out[i] = num / den;
  }
  return out;
}

/* ────── avg_line — blend of EMA(200), SMA(20), RQ kernel ────── */
export interface AvgLineResult {
  src: Array<number | null>;
  avgLine: Array<number | null>;
  bbUpper: Array<number | null>;
  bbMid: Array<number | null>;
  bbLower: Array<number | null>;
  /* Raw components — used by the Combined Envelope blend (Pine parity). */
  emaSrc: Array<number | null>;
  smaSrc: Array<number | null>;
  kSrc: Array<number | null>;
  bbLen: number;
  bbMult: number;
}

export function computeEnvelope(candles: CandleRow[], opts?: {
  lrLen?: number;       // default 14
  bbLen?: number;       // default 20
  bbMult?: number;      // default 2
  emaLen?: number;      // default 200
  kH?: number;          // RQ h, default 8
  kR?: number;          // RQ r, default 8.0
  kX?: number;          // RQ start bar, default 25
}): AvgLineResult {
  const lrLen = opts?.lrLen ?? 14;
  const bbLen = opts?.bbLen ?? 20;
  const bbMult = opts?.bbMult ?? 2;
  const emaLen = opts?.emaLen ?? 200;
  const kH = opts?.kH ?? 8;
  const kR = opts?.kR ?? 8.0;
  const kX = opts?.kX ?? 25;

  const opens = candles.map(c => c.open);
  const closes = candles.map(c => c.close);
  const highs = candles.map(c => c.high);
  const lows = candles.map(c => c.low);

  const lrOpen = linreg(opens, lrLen);
  const lrClose = linreg(closes, lrLen);

  // clr_src = (lr_o + high + low + lr_c) / 4
  const src: Array<number | null> = candles.map((_, i) => {
    const lo = lrOpen[i], lc = lrClose[i];
    if (lo == null || lc == null) return null;
    return (lo + highs[i] + lows[i] + lc) / 4;
  });

  // R108 — Pine-faithful EMA (value from bar 1) so the LTF envelope matches
  // TradingView even with limited history; converges identically once warm.
  const emaSrc = emaSeeded(src, emaLen);
  const smaSrc = sma(src, bbLen);
  const kSrc = rqKernel(src, kH, kR, kX);

  const avgLine: Array<number | null> = candles.map((_, i) => {
    const a = emaSrc[i], b = smaSrc[i], c = kSrc[i];
    if (a == null || b == null || c == null) return null;
    return (a + b + c) / 3;
  });

  // Round 35 — match Pine exactly:
  //   avg_bb_basis = sma(avg_line, _BB_LEN)
  //   avg_bb_dev   = _BB_MULT * stdev(avg_line, _BB_LEN)
  //   avg_bb_upper = basis + dev
  //   avg_bb_lower = basis - dev
  // Previously we used the RAW avg_line as the basis and the std-deviation
  // of `src` around avg_line for the band width. That produced a much
  // jittery basis (it was effectively the unsmoothed blended line), which
  // in turn made combined_bb_mid jitter and the AvgCloud direction flip
  // frequently — visible as the choppy cloud the user reported vs. the
  // smooth single-block cloud on TradingView.
  const bbBasis = sma(avgLine, bbLen);
  const bbUpper: Array<number | null> = new Array(candles.length).fill(null);
  const bbLower: Array<number | null> = new Array(candles.length).fill(null);
  for (let i = bbLen - 1; i < candles.length; i++) {
    const basis = bbBasis[i];
    if (basis == null) continue;
    let s = 0, n = 0;
    for (let k = i - bbLen + 1; k <= i; k++) {
      const v = avgLine[k];
      if (v == null) continue;
      s += (v - basis) * (v - basis);
      n++;
    }
    if (n >= bbLen) {
      const sd = Math.sqrt(s / bbLen);
      bbUpper[i] = basis + bbMult * sd;
      bbLower[i] = basis - bbMult * sd;
    }
  }

  return { src, avgLine, bbUpper, bbMid: bbBasis, bbLower, emaSrc, smaSrc, kSrc, bbLen, bbMult };
}

/* ────── combined envelope from LTF + CTF results, aligned by time ────── */
export interface CombinedEnvelope {
  times: number[];
  upper: Array<number | null>;
  lower: Array<number | null>;
  mid: Array<number | null>;
}

/**
 * Project CTF series onto LTF timeline (step-hold: at each LTF time t, use
 * the latest CTF bar whose time ≤ t).
 */
function projectToLtf(
  ltfTimes: number[],
  ctfTimes: number[],
  ctfValues: Array<number | null>
): Array<number | null> {
  const out: Array<number | null> = new Array(ltfTimes.length).fill(null);
  let j = 0;
  for (let i = 0; i < ltfTimes.length; i++) {
    while (j + 1 < ctfTimes.length && ctfTimes[j + 1] <= ltfTimes[i]) j++;
    if (ctfTimes.length > 0 && ctfTimes[j] <= ltfTimes[i]) {
      out[i] = ctfValues[j] ?? null;
    }
  }
  return out;
}

/**
 * CTF avg-line + band — Pine's `f_ctf_context` (JSR.txt lines 191–211):
 *
 *   clr_src_ctf   = (linreg(open,14) + high + low + linreg(close,14)) / 4    (at CTF cadence)
 *   clr_src_htf   = (linreg(open,14) + high + low + linreg(close,14)) / 4    (at HTF cadence)
 *   ema_ctf, sma_ctf, kern_ctf  computed on clr_src_ctf
 *   ema_htf, sma_htf, kern_htf  computed on clr_src_htf (then step-projected onto CTF cadence)
 *   avg_line  = ((ema_ctf + ema_htf + sma_ctf + sma_htf) / 4 + kern_ctf + kern_htf) / 3
 *   avg_basis = sma(avg_line, 20)
 *   avg_dev   = 2 * stdev(avg_line, 20)
 *   upper/lower = avg_basis ± avg_dev
 *
 * Output is at CTF cadence; caller must step-project onto the LTF timeline
 * before combining with the LTF band (mirrors Pine's outer security request
 * with `lookahead=barmerge.lookahead_off`).
 *
 * Round 70 — restores the CTF half of Combined Midline. Round 41 had
 * collapsed CMid to LTF-only because EMA(200) at CTF cadence wasn't
 * converging on the small CTF history we fetched. The fix is in two
 * places: (1) this faithful implementation, (2) ChartsPage now fetches
 * ≥1000 CTF/HTF bars when CMid is enabled so the EMA actually warms up.
 * When HTF data is missing at a bar (still warming or no aux passed in),
 * we return null for that bar — strictly Pine-faithful — and the combiner
 * gracefully falls back to LTF alone there.
 */
export interface CtfAvgLineResult {
  ctfTimes: number[];
  avgLine: Array<number | null>;
  bbBasis: Array<number | null>;
  bbUpper: Array<number | null>;
  bbLower: Array<number | null>;
}

export function computeCtfAvgLine(
  ctfCandles: CandleRow[],
  htfCandles: CandleRow[],
  opts?: {
    lrLen?: number; bbLen?: number; bbMult?: number;
    emaLen?: number; kH?: number; kR?: number; kX?: number;
  }
): CtfAvgLineResult {
  const lrLen = opts?.lrLen ?? 14;
  const bbLen = opts?.bbLen ?? 20;
  const bbMult = opts?.bbMult ?? 2;
  const emaLen = opts?.emaLen ?? 200;
  const kH = opts?.kH ?? 8;
  const kR = opts?.kR ?? 8.0;
  const kX = opts?.kX ?? 25;

  const ctfTimes = ctfCandles.map(c => c.time);
  const n = ctfTimes.length;

  // CTF series + components
  const ctfO = ctfCandles.map(c => c.open);
  const ctfC = ctfCandles.map(c => c.close);
  const ctfH = ctfCandles.map(c => c.high);
  const ctfL = ctfCandles.map(c => c.low);
  const ctfLrO = linreg(ctfO, lrLen);
  const ctfLrC = linreg(ctfC, lrLen);
  const ctfClr: Array<number | null> = ctfCandles.map((_, i) => {
    const lo = ctfLrO[i], lc = ctfLrC[i];
    if (lo == null || lc == null) return null;
    return (lo + ctfH[i] + ctfL[i] + lc) / 4;
  });
  const ctfEma = emaSeeded(ctfClr, emaLen);   // R108 — Pine-faithful (value from bar 1)
  const ctfSma = sma(ctfClr, bbLen);
  const ctfKern = rqKernel(ctfClr, kH, kR, kX);

  // HTF series + components (computed at HTF cadence)
  const htfTimes = htfCandles.map(c => c.time);
  let htfEma: Array<number | null> = [];
  let htfSma: Array<number | null> = [];
  let htfKern: Array<number | null> = [];
  if (htfCandles.length > 0) {
    const htfO = htfCandles.map(c => c.open);
    const htfC = htfCandles.map(c => c.close);
    const htfH2 = htfCandles.map(c => c.high);
    const htfL2 = htfCandles.map(c => c.low);
    const htfLrO = linreg(htfO, lrLen);
    const htfLrC = linreg(htfC, lrLen);
    const htfClr: Array<number | null> = htfCandles.map((_, i) => {
      const lo = htfLrO[i], lc = htfLrC[i];
      if (lo == null || lc == null) return null;
      return (lo + htfH2[i] + htfL2[i] + lc) / 4;
    });
    htfEma = emaSeeded(htfClr, emaLen);   // R108 — Pine-faithful (value from bar 1)
    htfSma = sma(htfClr, bbLen);
    htfKern = rqKernel(htfClr, kH, kR, kX);
  }

  // Step-hold the HTF components onto the CTF timeline. This mirrors
  // Pine's inner `request.security(..., _htf_inner, ...)` semantics.
  const htfEmaOnCtf  = projectToLtf(ctfTimes, htfTimes, htfEma);
  const htfSmaOnCtf  = projectToLtf(ctfTimes, htfTimes, htfSma);
  const htfKernOnCtf = projectToLtf(ctfTimes, htfTimes, htfKern);

  // avg_line at CTF cadence — exact 6-component Pine blend. Strict-NULL:
  // if any component is missing, the bar is null. The combiner falls back
  // to LTF-only at such bars so we never paint a wrong value.
  const avgLine: Array<number | null> = new Array(n).fill(null);
  for (let i = 0; i < n; i++) {
    const ec = ctfEma[i], sc = ctfSma[i], kc = ctfKern[i];
    const eh = htfEmaOnCtf[i], sh = htfSmaOnCtf[i], kh = htfKernOnCtf[i];
    if (ec == null || sc == null || kc == null) continue;
    if (eh == null || sh == null || kh == null) continue;
    avgLine[i] = ((ec + eh + sc + sh) / 4 + kc + kh) / 3;
  }

  // SMA(avgLine, bbLen) ± bbMult * StDev(avgLine, bbLen)
  const bbBasis = sma(avgLine, bbLen);
  const bbUpper: Array<number | null> = new Array(n).fill(null);
  const bbLower: Array<number | null> = new Array(n).fill(null);
  for (let i = bbLen - 1; i < n; i++) {
    const basis = bbBasis[i];
    if (basis == null) continue;
    let s = 0, c = 0;
    for (let k = i - bbLen + 1; k <= i; k++) {
      const v = avgLine[k];
      if (v == null) continue;
      s += (v - basis) * (v - basis);
      c++;
    }
    if (c >= bbLen) {
      const sd = Math.sqrt(s / bbLen);
      bbUpper[i] = basis + bbMult * sd;
      bbLower[i] = basis - bbMult * sd;
    }
  }

  return { ctfTimes, avgLine, bbBasis, bbUpper, bbLower };
}

/**
 * Combined envelope — Pine JSR (lines 295–298):
 *    combined_bb_upper = math.max(avg_bb_upper_ltf, avg_bb_upper_ctf)
 *    combined_bb_lower = math.min(avg_bb_lower_ltf, avg_bb_lower_ctf)
 *    combined_bb_mid   = (combined_bb_upper + combined_bb_lower) / 2
 *
 * Round 70 — proper Pine-faithful implementation, replacing the LTF-only
 * fallback from Round 41. The CTF band (from computeCtfAvgLine, which
 * itself blends CTF + HTF) is step-projected onto the LTF timeline, then
 * combined with the LTF band per LTF bar. When the CTF band isn't
 * available at a bar (still warming up, or no aux candles supplied),
 * we use the LTF band alone — same robustness Round 41 was after, but
 * only invoked when the CTF data genuinely isn't ready, not always.
 */
export function computeCombinedEnvelope(
  ltfCandles: CandleRow[],
  ltfEnv: AvgLineResult | undefined,
  ctfCandles?: CandleRow[],
  htfCandles?: CandleRow[],
): CombinedEnvelope {
  const ltfTimes = ltfCandles.map(c => c.time);
  const n = ltfTimes.length;

  // CTF band on the LTF timeline. Requires both CTF and HTF candles (Pine's
  // f_ctf_context needs both); without one we fall back to LTF-only at
  // every bar — the Round 41 behaviour.
  let ctfBandUpperOnLtf: Array<number | null> = new Array(n).fill(null);
  let ctfBandLowerOnLtf: Array<number | null> = new Array(n).fill(null);
  if (ctfCandles && htfCandles && ctfCandles.length > 0 && htfCandles.length > 0) {
    const ctfRes = computeCtfAvgLine(ctfCandles, htfCandles);
    ctfBandUpperOnLtf = projectToLtf(ltfTimes, ctfRes.ctfTimes, ctfRes.bbUpper);
    ctfBandLowerOnLtf = projectToLtf(ltfTimes, ctfRes.ctfTimes, ctfRes.bbLower);
  }

  const upper: Array<number | null> = new Array(n).fill(null);
  const lower: Array<number | null> = new Array(n).fill(null);
  const mid:   Array<number | null> = new Array(n).fill(null);
  for (let i = 0; i < n; i++) {
    const ltfU = ltfEnv?.bbUpper[i] ?? null;
    const ltfL = ltfEnv?.bbLower[i] ?? null;
    if (ltfU == null || ltfL == null) continue;
    const ctfU = ctfBandUpperOnLtf[i];
    const ctfL = ctfBandLowerOnLtf[i];
    // Pine: combined = max(ltf, ctf) / min(ltf, ctf). When CTF unavailable
    // (warming up, fresh listing, or no aux data), LTF band alone.
    const u = ctfU != null ? Math.max(ltfU, ctfU) : ltfU;
    const l = ctfL != null ? Math.min(ltfL, ctfL) : ltfL;
    upper[i] = u;
    lower[i] = l;
    mid[i] = (u + l) / 2;
  }

  return { times: ltfTimes, upper, lower, mid };
}

/* ────── CPR (TC / BC / Pivot) derived from previous period ──────
   periodType: 'D' → daily, 'W' → weekly, 'M' → monthly.
   Groups CTF-level bars by period, takes previous period's H/L/C,
   computes TC/BC, and stamps onto the current period's bars. */
export type CprPeriod = 'D' | 'W' | 'M';

export interface CprPoint {
  time: number;      // bar time (IST seconds)
  tc: number;
  bc: number;
  pivot: number;
  direction: 'rising' | 'falling' | 'flat' | 'cross' | 'narrow';
  // Round 33 — band-rise direction relative to the previous period's TC/BC.
  // Rising  : (curTc > prevTc OR curBc > prevBc) AND NOT (curTc < prevTc OR curBc < prevBc)
  // Falling : (curTc < prevTc OR curBc < prevBc) AND NOT (curTc > prevTc OR curBc > prevBc)
  // Neutral : otherwise (or no previous period to compare).
  bandDir?: 'rising' | 'falling' | 'neutral';
  /** Period key (e.g. '2026-05-13' for daily) — useful for segmenting bands by period. */
  periodKey?: string;
}

/** Exchange-specific IST day-start seconds (session open). CPR / POC / session
 *  groupings anchor to this time-of-day so that "today's" bars cover 09:15 IST →
 *  next 09:14 IST for NSE, not midnight→midnight. */
export const DAY_START_IST_SEC: Record<string, number> = {
  NSE:   9 * 3600 + 15 * 60,   // 09:15
  NFO:   9 * 3600 + 15 * 60,
  BFO:   9 * 3600 + 15 * 60,
  BSE:   9 * 3600 + 15 * 60,
  MCX:   9 * 3600,              // 09:00
  DELTA: 5 * 3600 + 30 * 60,    // 05:30
};

function dayStartSec(exchange?: string): number {
  if (!exchange) return DAY_START_IST_SEC.NSE;
  const key = exchange.toUpperCase();
  return DAY_START_IST_SEC[key] ?? DAY_START_IST_SEC.NSE;
}

function periodKey(timeIstSec: number, kind: CprPeriod, exchange?: string): string {
  // Shift bars by -day-start so that bucket rollover happens at session open,
  // not at 00:00 IST. time is IST-shifted seconds; convert to IST calendar.
  const effective = timeIstSec - dayStartSec(exchange);
  const d = new Date(effective * 1000);
  const yyyy = d.getUTCFullYear();
  const mm = d.getUTCMonth();
  const dd = d.getUTCDate();
  if (kind === 'D') return `${yyyy}-${mm}-${dd}`;
  if (kind === 'M') return `${yyyy}-${mm}`;
  // ISO week — use Thursday rule
  const jan1 = Date.UTC(yyyy, 0, 1) / 1000;
  const days = Math.floor((Date.UTC(yyyy, mm, dd) / 1000 - jan1) / 86400);
  const wk = Math.floor((days + ((new Date(Date.UTC(yyyy, 0, 1))).getUTCDay() || 7) - 1) / 7);
  return `${yyyy}-W${wk}`;
}

export function computeCpr(candles: CandleRow[], period: CprPeriod, exchange?: string): CprPoint[] {
  if (!candles.length) return [];
  // Collect per-period high/low/close
  const groups: Array<{ key: string; startIdx: number; endIdx: number; h: number; l: number; c: number }> = [];
  let cur: typeof groups[0] | null = null;
  for (let i = 0; i < candles.length; i++) {
    const k = periodKey(candles[i].time, period, exchange);
    if (!cur || cur.key !== k) {
      if (cur) groups.push(cur);
      cur = { key: k, startIdx: i, endIdx: i, h: candles[i].high, l: candles[i].low, c: candles[i].close };
    } else {
      cur.endIdx = i;
      cur.h = Math.max(cur.h, candles[i].high);
      cur.l = Math.min(cur.l, candles[i].low);
      cur.c = candles[i].close;
    }
  }
  if (cur) groups.push(cur);

  const pts: CprPoint[] = [];
  let prevWidth: number | null = null;
  let prevTc: number | null = null;
  let prevBc: number | null = null;
  for (let g = 1; g < groups.length; g++) {
    const prev = groups[g - 1];
    const pivot = (prev.h + prev.l + prev.c) / 3;
    const bc = (prev.h + prev.l) / 2;
    const tc = pivot + (pivot - bc);
    const width = Math.abs(tc - bc);

    let direction: CprPoint['direction'] = 'flat';
    if (prevWidth != null) {
      const riseThresh = 0.10 * prevWidth;
      const diff = width - prevWidth;
      if (diff > riseThresh) direction = 'rising';
      else if (diff < -riseThresh) direction = 'falling';
      else direction = 'flat';
    }
    // narrow classification if width < 20% of period range
    const pr = prev.h - prev.l;
    if (pr > 0 && width / pr < 0.2) direction = 'narrow';

    // Round 33 — band-rise direction (relative to PREVIOUS period's band, not the
    // current group's source candles). For the first period we have no prior
    // band → neutral.
    const curTcTop = Math.max(tc, bc);
    const curBcBot = Math.min(tc, bc);
    let bandDir: CprPoint['bandDir'] = 'neutral';
    if (prevTc != null && prevBc != null) {
      const prevTop = Math.max(prevTc, prevBc);
      const prevBot = Math.min(prevTc, prevBc);
      const topUp   = curTcTop > prevTop;
      const topDown = curTcTop < prevTop;
      const botUp   = curBcBot > prevBot;
      const botDown = curBcBot < prevBot;
      const anyUp   = topUp || botUp;
      const anyDown = topDown || botDown;
      if (anyUp && !anyDown) bandDir = 'rising';
      else if (anyDown && !anyUp) bandDir = 'falling';
      else bandDir = 'neutral';
    }

    const cur = groups[g];
    for (let k = cur.startIdx; k <= cur.endIdx; k++) {
      pts.push({
        time: candles[k].time, tc: curTcTop, bc: curBcBot, pivot,
        direction, bandDir, periodKey: cur.key,
      });
    }
    prevWidth = width;
    prevTc = curTcTop;
    prevBc = curBcBot;
  }
  return pts;
}

/**
 * Round 33 — projected next-period CPR.
 *
 * Computes the CPR that would apply to the NEXT period if the CURRENT period
 * closes right now. Uses the running high / low / close of the current
 * (incomplete) period as the source HLC. Returns `null` when there is no
 * data for the current period (e.g. trading hasn't started yet).
 *
 * Caller is responsible for choosing how to render this — typically a band
 * from the END of the current period forward through one full period span.
 */
export interface ProjectedCpr {
  tcTop: number;
  bcBot: number;
  pivot: number;
  /** UNIX seconds (IST-shifted) — the boundary at which the current period ends. */
  periodEndTime: number;
  /** Best-estimate UNIX seconds at which the projected period ends. */
  projectionEndTime: number;
  /** Direction of this projected band relative to the current period's actual band. */
  bandDir: 'rising' | 'falling' | 'neutral';
}

export function computeProjectedCpr(
  candles: CandleRow[],
  period: CprPeriod,
  exchange?: string,
): ProjectedCpr | null {
  if (!candles.length) return null;

  // Walk back from the latest candle until the periodKey changes — that gives
  // the bar range covering the CURRENT (developing) period.
  const last = candles[candles.length - 1];
  const lastKey = periodKey(last.time, period, exchange);
  let startIdx = candles.length - 1;
  while (startIdx > 0 && periodKey(candles[startIdx - 1].time, period, exchange) === lastKey) {
    startIdx--;
  }
  let h = candles[startIdx].high;
  let l = candles[startIdx].low;
  let c = candles[candles.length - 1].close;
  for (let i = startIdx; i < candles.length; i++) {
    if (candles[i].high > h) h = candles[i].high;
    if (candles[i].low  < l) l = candles[i].low;
  }
  const pivot = (h + l + c) / 3;
  const bc    = (h + l) / 2;
  const tc    = pivot + (pivot - bc);
  const tcTop = Math.max(tc, bc);
  const bcBot = Math.min(tc, bc);

  // Find the CURRENT period's actual CPR (computed from the period BEFORE the
  // developing one) so we can classify the projected band's direction relative
  // to what is currently being plotted on-screen.
  let prevTcTop: number | null = null;
  let prevBcBot: number | null = null;
  if (startIdx > 0) {
    // Need at least two periods of history before the developing one.
    let prevEnd = startIdx - 1;
    const prevKey = periodKey(candles[prevEnd].time, period, exchange);
    let prevStart = prevEnd;
    while (prevStart > 0 && periodKey(candles[prevStart - 1].time, period, exchange) === prevKey) {
      prevStart--;
    }
    if (prevStart > 0) {
      // The CPR plotted for the developing period was computed from periodA
      // (period right before "previous"), so for direction we compare against
      // the current period's plotted TC/BC. Easier: compare to the previous
      // period's bar-derived TC/BC directly.
      let ph = candles[prevStart].high;
      let pl = candles[prevStart].low;
      let pc = candles[prevEnd].close;
      for (let i = prevStart; i <= prevEnd; i++) {
        if (candles[i].high > ph) ph = candles[i].high;
        if (candles[i].low  < pl) pl = candles[i].low;
      }
      const pPivot = (ph + pl + pc) / 3;
      const pBc    = (ph + pl) / 2;
      const pTc    = pPivot + (pPivot - pBc);
      prevTcTop = Math.max(pTc, pBc);
      prevBcBot = Math.min(pTc, pBc);
    }
  }

  let bandDir: ProjectedCpr['bandDir'] = 'neutral';
  if (prevTcTop != null && prevBcBot != null) {
    const topUp   = tcTop > prevTcTop;
    const topDown = tcTop < prevTcTop;
    const botUp   = bcBot > prevBcBot;
    const botDown = bcBot < prevBcBot;
    const anyUp   = topUp || botUp;
    const anyDown = topDown || botDown;
    if (anyUp && !anyDown) bandDir = 'rising';
    else if (anyDown && !anyUp) bandDir = 'falling';
    else bandDir = 'neutral';
  }

  // Project period end timestamp from period kind. We over-estimate slightly
  // for weekly/monthly so the band visibly extends into the future even on
  // higher LTFs where the chart's right-edge is near the live bar.
  const lastT = last.time;
  const startT = candles[startIdx].time;
  const elapsedSec = lastT - startT;
  let fullPeriodSec: number;
  if (period === 'D')      fullPeriodSec = 24 * 3600;
  else if (period === 'W') fullPeriodSec = 7  * 24 * 3600;
  else                     fullPeriodSec = 30 * 24 * 3600;
  const periodEndTime  = startT + fullPeriodSec;
  const projectionEndTime = periodEndTime + fullPeriodSec;

  return { tcTop, bcBot, pivot, periodEndTime, projectionEndTime, bandDir };
}

/* ────── POC (volume profile argmax over a window) ──────
   For each bar in the input, take `lookback` candles including it,
   build 25-row volume profile between [min low, max high], return
   the row center as the POC for that bar. */
export interface PocPoint {
  time: number;
  poc: number;
  top: number;
  bottom: number;
  // Round 33 — band-rise direction relative to the previous period's POC band.
  // Same Rising/Falling/Neutral logic as CprPoint.bandDir.
  bandDir?: 'rising' | 'falling' | 'neutral';
  periodKey?: string;
}

export function computePoc(candles: CandleRow[], lookback: number, rows = 25): PocPoint[] {
  const out: PocPoint[] = [];
  for (let i = lookback - 1; i < candles.length; i++) {
    let pHi = -Infinity, pLo = Infinity;
    for (let k = i - lookback + 1; k <= i; k++) {
      if (candles[k].high > pHi) pHi = candles[k].high;
      if (candles[k].low < pLo) pLo = candles[k].low;
    }
    if (!isFinite(pHi) || !isFinite(pLo) || pHi <= pLo) continue;
    const step = (pHi - pLo) / rows;
    const profile = new Array<number>(rows).fill(0);
    for (let k = i - lookback + 1; k <= i; k++) {
      const c = candles[k];
      const barTop = Math.min(c.high, pHi);
      const barBot = Math.max(c.low, pLo);
      if (barTop <= barBot) continue;
      const fromRow = Math.max(0, Math.floor((barBot - pLo) / step));
      const toRow = Math.min(rows - 1, Math.floor((barTop - pLo) / step));
      const rowsTouched = Math.max(1, toRow - fromRow + 1);
      const per = (c.volume || 0) / rowsTouched;
      for (let r = fromRow; r <= toRow; r++) profile[r] += per;
    }
    let maxR = 0, maxV = -Infinity;
    for (let r = 0; r < rows; r++) { if (profile[r] > maxV) { maxV = profile[r]; maxR = r; } }
    const bottom = pLo + maxR * step;
    const top = bottom + step;
    out.push({ time: candles[i].time, poc: (top + bottom) / 2, top, bottom });
  }
  return out;
}

/* ────── Per-period POC (CPR-aligned volume profile) ──────
 *
 * Round 32 — replaces the sliding-window POC that produced bands which
 * did not align with CPR period boundaries. This variant mirrors
 * computeCpr exactly:
 *
 *   1. Group the input candles by `period` (D/W/M) using the same
 *      exchange-anchored periodKey as CPR.
 *   2. For each period g (g ≥ 1), build a 25-row volume profile from
 *      period g-1's candles (PREVIOUS period — same semantics as CPR's
 *      "previous period drives this period's TC/BC/pivot").
 *   3. Stamp the resulting POC top/bottom onto every candle in period g
 *      so the projected band step-holds across the entire period and
 *      starts/ends exactly where the CPR rectangle does.
 *
 * Inputs should be the LTF (chart) candles so the volume profile gets
 * many samples per period (e.g. ~5 daily bars per weekly CPR). Using
 * higher-TF candles often left only 1 sample per period, collapsing
 * the profile to a single row.
 */
export function computePocPerPeriod(
  candles: CandleRow[],
  period: CprPeriod,
  exchange?: string,
  rows = 25,
): PocPoint[] {
  if (!candles.length) return [];

  // Group by period (same logic as computeCpr).
  type Group = { key: string; startIdx: number; endIdx: number };
  const groups: Group[] = [];
  let cur: Group | null = null;
  for (let i = 0; i < candles.length; i++) {
    const k = periodKey(candles[i].time, period, exchange);
    if (!cur || cur.key !== k) {
      if (cur) groups.push(cur);
      cur = { key: k, startIdx: i, endIdx: i };
    } else {
      cur.endIdx = i;
    }
  }
  if (cur) groups.push(cur);

  const out: PocPoint[] = [];

  // Round 33 — track the previous period's plotted band so we can classify
  // the current period as Rising / Falling / Neutral.
  let lastBandTop: number | null = null;
  let lastBandBot: number | null = null;

  // Build POC band from PREVIOUS period's candles, stamp onto current period.
  for (let g = 1; g < groups.length; g++) {
    const prev = groups[g - 1];
    const cur  = groups[g];

    // Establish profile range from prev period's H/L.
    let pHi = -Infinity, pLo = Infinity;
    for (let k = prev.startIdx; k <= prev.endIdx; k++) {
      if (candles[k].high > pHi) pHi = candles[k].high;
      if (candles[k].low  < pLo) pLo = candles[k].low;
    }
    if (!isFinite(pHi) || !isFinite(pLo) || pHi <= pLo) continue;

    const step = (pHi - pLo) / rows;
    const profile = new Array<number>(rows).fill(0);
    for (let k = prev.startIdx; k <= prev.endIdx; k++) {
      const c = candles[k];
      const barTop = Math.min(c.high, pHi);
      const barBot = Math.max(c.low, pLo);
      if (barTop <= barBot) continue;
      const fromRow = Math.max(0, Math.floor((barBot - pLo) / step));
      const toRow   = Math.min(rows - 1, Math.floor((barTop - pLo) / step));
      const rowsTouched = Math.max(1, toRow - fromRow + 1);
      // Spread bar volume equally across all rows it touches. Bars without
      // a `volume` field still contribute 1 unit so the profile remains
      // meaningful for instruments where the candle endpoint omits volume.
      const vol = (c.volume && c.volume > 0) ? c.volume : 1;
      const per = vol / rowsTouched;
      for (let r = fromRow; r <= toRow; r++) profile[r] += per;
    }

    let maxR = 0, maxV = -Infinity;
    for (let r = 0; r < rows; r++) {
      if (profile[r] > maxV) { maxV = profile[r]; maxR = r; }
    }
    const bottom = pLo + maxR * step;
    const top    = bottom + step;

    // Round 33 — directional classification relative to the previous period's POC band.
    let bandDir: PocPoint['bandDir'] = 'neutral';
    if (lastBandTop != null && lastBandBot != null) {
      const topUp   = top > lastBandTop;
      const topDown = top < lastBandTop;
      const botUp   = bottom > lastBandBot;
      const botDown = bottom < lastBandBot;
      const anyUp   = topUp || botUp;
      const anyDown = topDown || botDown;
      if (anyUp && !anyDown) bandDir = 'rising';
      else if (anyDown && !anyUp) bandDir = 'falling';
      else bandDir = 'neutral';
    }
    lastBandTop = top;
    lastBandBot = bottom;

    // Stamp this period's POC band onto every LTF bar in the period.
    for (let k = cur.startIdx; k <= cur.endIdx; k++) {
      out.push({
        time: candles[k].time, poc: (top + bottom) / 2, top, bottom,
        bandDir, periodKey: cur.key,
      });
    }
  }

  return out;
}

/**
 * Round 33 — projected next-period POC.
 *
 * Builds a volume profile from the CURRENT (developing) period's LTF candles
 * and returns the resulting POC top/bottom + the projection time window. Same
 * shape as computeProjectedCpr so the chart code can use them interchangeably.
 */
export interface ProjectedPoc {
  top: number;
  bottom: number;
  periodEndTime: number;
  projectionEndTime: number;
  bandDir: 'rising' | 'falling' | 'neutral';
}

export function computeProjectedPoc(
  candles: CandleRow[],
  period: CprPeriod,
  exchange?: string,
  rows = 25,
): ProjectedPoc | null {
  if (!candles.length) return null;

  const last = candles[candles.length - 1];
  const lastKey = periodKey(last.time, period, exchange);
  let startIdx = candles.length - 1;
  while (startIdx > 0 && periodKey(candles[startIdx - 1].time, period, exchange) === lastKey) {
    startIdx--;
  }
  let pHi = -Infinity, pLo = Infinity;
  for (let i = startIdx; i < candles.length; i++) {
    if (candles[i].high > pHi) pHi = candles[i].high;
    if (candles[i].low  < pLo) pLo = candles[i].low;
  }
  if (!isFinite(pHi) || !isFinite(pLo) || pHi <= pLo) return null;

  const step = (pHi - pLo) / rows;
  const profile = new Array<number>(rows).fill(0);
  for (let i = startIdx; i < candles.length; i++) {
    const c = candles[i];
    const barTop = Math.min(c.high, pHi);
    const barBot = Math.max(c.low, pLo);
    if (barTop <= barBot) continue;
    const fromRow = Math.max(0, Math.floor((barBot - pLo) / step));
    const toRow   = Math.min(rows - 1, Math.floor((barTop - pLo) / step));
    const rowsTouched = Math.max(1, toRow - fromRow + 1);
    const vol = (c.volume && c.volume > 0) ? c.volume : 1;
    const per = vol / rowsTouched;
    for (let r = fromRow; r <= toRow; r++) profile[r] += per;
  }
  let maxR = 0, maxV = -Infinity;
  for (let r = 0; r < rows; r++) {
    if (profile[r] > maxV) { maxV = profile[r]; maxR = r; }
  }
  const bottom = pLo + maxR * step;
  const top    = bottom + step;

  // Compare against the immediately previous period's actual POC band so the
  // projected band direction tracks the same comparison the user sees on screen.
  let prevTop: number | null = null;
  let prevBot: number | null = null;
  if (startIdx > 0) {
    let prevEnd = startIdx - 1;
    const prevKey = periodKey(candles[prevEnd].time, period, exchange);
    let prevStart = prevEnd;
    while (prevStart > 0 && periodKey(candles[prevStart - 1].time, period, exchange) === prevKey) {
      prevStart--;
    }
    let qHi = -Infinity, qLo = Infinity;
    for (let i = prevStart; i <= prevEnd; i++) {
      if (candles[i].high > qHi) qHi = candles[i].high;
      if (candles[i].low  < qLo) qLo = candles[i].low;
    }
    if (isFinite(qHi) && isFinite(qLo) && qHi > qLo) {
      const qStep = (qHi - qLo) / rows;
      const qProfile = new Array<number>(rows).fill(0);
      for (let i = prevStart; i <= prevEnd; i++) {
        const c = candles[i];
        const barTop = Math.min(c.high, qHi);
        const barBot = Math.max(c.low, qLo);
        if (barTop <= barBot) continue;
        const fromRow = Math.max(0, Math.floor((barBot - qLo) / qStep));
        const toRow   = Math.min(rows - 1, Math.floor((barTop - qLo) / qStep));
        const rowsTouched = Math.max(1, toRow - fromRow + 1);
        const vol = (c.volume && c.volume > 0) ? c.volume : 1;
        const per = vol / rowsTouched;
        for (let r = fromRow; r <= toRow; r++) qProfile[r] += per;
      }
      let qMaxR = 0, qMaxV = -Infinity;
      for (let r = 0; r < rows; r++) {
        if (qProfile[r] > qMaxV) { qMaxV = qProfile[r]; qMaxR = r; }
      }
      prevBot = qLo + qMaxR * qStep;
      prevTop = prevBot + qStep;
    }
  }
  let bandDir: ProjectedPoc['bandDir'] = 'neutral';
  if (prevTop != null && prevBot != null) {
    const topUp   = top > prevTop;
    const topDown = top < prevTop;
    const botUp   = bottom > prevBot;
    const botDown = bottom < prevBot;
    const anyUp   = topUp || botUp;
    const anyDown = topDown || botDown;
    if (anyUp && !anyDown) bandDir = 'rising';
    else if (anyDown && !anyUp) bandDir = 'falling';
    else bandDir = 'neutral';
  }

  const lastT  = last.time;
  const startT = candles[startIdx].time;
  let fullPeriodSec: number;
  if (period === 'D')      fullPeriodSec = 24 * 3600;
  else if (period === 'W') fullPeriodSec = 7  * 24 * 3600;
  else                     fullPeriodSec = 30 * 24 * 3600;
  const periodEndTime  = startT + fullPeriodSec;
  const projectionEndTime = periodEndTime + fullPeriodSec;
  return { top, bottom, periodEndTime, projectionEndTime, bandDir };
}

/* ────── ATR ────── */
/**
 * ATR(period) as a simple rolling mean of True Range — the definition already
 * used inline by the BOLev narrow-zone check in TradingChart.
 *
 * Exported here, beside sma / stdDev / ema / linreg, so the BW trade marks and
 * the script that verifies them call the SAME function. A stop drawn from one
 * ATR and validated against another is not validated at all.
 *
 * `null` until `period` bars of True Range exist; TR[0] is high-low, since
 * there is no previous close to gap from.
 */
export function atrSeries(candles: CandleRow[], period = 14): Array<number | null> {
  const n = candles.length;
  const out: Array<number | null> = new Array(n).fill(null);
  if (!n) return out;
  const tr: number[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const c = candles[i];
    const prevC = i > 0 ? candles[i - 1].close : c.close;
    tr[i] = Math.max(c.high - c.low, Math.abs(c.high - prevC), Math.abs(c.low - prevC));
  }
  let sum = 0;
  for (let i = 0; i < n; i++) {
    sum += tr[i];
    if (i >= period) sum -= tr[i - period];
    if (i >= period - 1) out[i] = sum / period;
  }
  return out;
}

/* ────── BBR100 reversal ────── */
export interface BbrReversals { overboughtAt: Set<number>; oversoldAt: Set<number>; }

/**
 * The BBR100 oscillator itself: %B of the HLC3 Bollinger band, rescaled so the
 * LOWER band reads -100, the basis 0 and the UPPER band +100. Values run outside
 * +/-100 whenever price is outside the bands (measured p1..p99 across 60 Delta
 * symbols: -133..+148), which is the point - +/-100 is the band, not a clamp.
 *
 * Extracted so the lower-pane oscillator and the candle tint are the SAME
 * numbers. computeBbrReversals below calls it, so the curve on screen and the
 * bars it recolours can never drift apart.
 */
export function computeBbrSeries(candles: CandleRow[], bbLen = 20, bbMult = 2): Array<number | null> {
  const src = candles.map(c => (c.high + c.low + c.close) / 3);
  const basis = sma(src, bbLen);
  const sd = stdDev(src, basis, bbLen);
  const bbr: Array<number | null> = new Array(candles.length).fill(null);
  for (let i = 0; i < candles.length; i++) {
    const b = basis[i], d = sd[i];
    if (b == null || d == null) continue;
    const upper = b + bbMult * d;
    const lower = b - bbMult * d;
    const range = upper - lower;
    bbr[i] = range !== 0 ? ((src[i] - lower) / range) * 200 - 100 : 0;
  }
  return bbr;
}

export function computeBbrReversals(candles: CandleRow[], bbLen = 20, bbMult = 2): BbrReversals {
  const bbr = computeBbrSeries(candles, bbLen, bbMult);
  const overboughtAt = new Set<number>();
  const oversoldAt = new Set<number>();
  for (let i = 1; i < candles.length; i++) {
    const cur = bbr[i], prev = bbr[i - 1];
    if (cur == null || prev == null) continue;
    // Overbought reversal: extreme zone (now or prev) AND now < prev
    if ((cur >= 100 || prev >= 100) && cur < prev) overboughtAt.add(candles[i].time);
    // Oversold reversal: extreme zone (now or prev) AND now > prev
    if ((cur <= -100 || prev <= -100) && cur > prev) oversoldAt.add(candles[i].time);
  }
  return { overboughtAt, oversoldAt };
}

/* ────── Volume spike ────── */
export function computeVolumeSpikes(candles: CandleRow[], lookback = 20, mult = 2): Set<number> {
  const vols = candles.map(c => c.volume || 0);
  const mean = sma(vols, lookback);
  const sd = stdDev(vols, mean, lookback);
  const out = new Set<number>();
  for (let i = 0; i < candles.length; i++) {
    const m = mean[i], d = sd[i];
    if (m == null || d == null) continue;
    if (vols[i] > m + mult * d) out.add(candles[i].time);
  }
  return out;
}

/* ────── CTF / HTF interval mapping (Round 39 — tuned to match Pine's ~5x
 *        factor while staying on broker-supported intervals) ──────
 *
 * Pine's `f_ctf_from_ltf` uses arbitrary minute strings (5/15/25/75/300/D/W),
 * but those non-standard intervals (75, 150, 300) aren't supported by
 * Zerodha or Delta — earlier attempts to fetch them crashed at 4xx.
 *
 * The PREVIOUS PrashantWeb mapping (60 / 240 / 1440 / 10080) was safe but
 * far too aggressive: 15m LTF → daily CTF means CTF EMA(200) averages
 * across ~200 days, pulling combined_bb_mid (= midpoint of max-min
 * combined envelope) tens-of-thousands of dollars above current price on
 * volatile assets. The Avg-Mid cloud then renders as a massive bearish
 * red block from `avg_top` up to `mid` — visible in the user's BTCUSD
 * screenshot as a red zone consuming the entire upper half of the chart.
 *
 * New mapping snaps to broker-supported intervals as close to Pine's
 * factor as practical:
 *   1m   → 5m    (Pine: 5m,   factor 5x)
 *   3m   → 15m   (Pine: 15m,  factor 5x)
 *   5m   → 30m   (Pine: 25m,  factor 6x — 30m supported by both brokers)
 *   15m  → 60m   (Pine: 75m,  factor 4x — 60m is the closest universal)
 *   30m  → 60m   (Pine: 150m, factor 2x)
 *   60m  → 240m  (Pine: 300m, factor 4x — 240m supported by Delta;
 *                  Zerodha resamples internally)
 *   240m → 1440m (Pine: D,    factor 6x)
 *   D    → 10080m(Pine: W,    factor 7x)
 *   W    → 10080m(Pine: M proxy)
 */
export function ctfFromLtf(ltfMin: number): number {
  if (ltfMin <= 1)    return 5;
  if (ltfMin <= 3)    return 15;
  if (ltfMin <= 5)    return 30;
  if (ltfMin <= 15)   return 60;
  if (ltfMin <= 30)   return 60;
  if (ltfMin <= 60)   return 240;
  if (ltfMin <= 240)  return 1440;
  if (ltfMin <= 1440) return 10080;
  return 10080;
}

/** CPR base period for a given LTF, matching the Pine resolution table above. */
export function cprBaseFromLtf(ltfMin: number): CprPeriod {
  if (ltfMin <= 15)  return 'D';
  if (ltfMin <= 240) return 'W';
  return 'M';
}

export function htfFromCtf(ctfMin: number, mult = 15): number {
  // HTF (for Combined Envelope) — cap to allowed intervals larger than CTF.
  // With the corrected CTF set (60/240/1440/10080), HTF snaps to 1440 or 10080.
  const allowed = HTF_LADDER;
  const want = ctfMin * mult;
  let best = allowed[0], bestDiff = Infinity;
  for (const a of allowed) {
    const d = Math.abs(a - want);
    if (d < bestDiff) { bestDiff = d; best = a; }
  }
  // Force HTF strictly larger than CTF. At or above weekly the HTF is the CTF
  // itself — the old `return 10080` gave a MONTHLY chart a WEEKLY higher
  // timeframe, below the chart it is supposed to sit above.
  if (ctfMin < 1440) return Math.max(1440, best);
  return Math.max(ctfMin, 10080);
}

/* ────── POC lookback in bars based on CPR period ────── */
export function cprLookbackBars(periodType: CprPeriod, intervalMin: number): number {
  const minutesInPeriod =
    periodType === 'D' ? 375 :              // NSE trading minutes in a day
    periodType === 'W' ? 375 * 5 :          // trading week
    375 * 22;                                // trading month
  return Math.max(20, Math.round(minutesInPeriod / intervalMin));
}

/** Returns the UNIX seconds (IST-shifted) of every session-open bar that
 *  falls inside the given candles. The grouping resolution depends on the
 *  chart's lower-timeframe (LTF) so the lines stay readable at higher
 *  intervals — at 1-day candles every-session lines would draw on every bar:
 *
 *    ltfMinutes ≤ 15            → day-start  (one line per session)
 *    15 < ltfMinutes ≤ 240 (4h) → week-start (Monday's session only)
 *    ltfMinutes > 240           → month-start (1st trading day of month)
 *
 *  Day-start anchor remains exchange-specific (NSE 09:15, MCX 09:00,
 *  DELTA 05:30). The "Monday" / "1st of month" check is done in IST shifted
 *  by the same anchor, so a Monday at NSE means the session that opens at
 *  09:15 IST Monday — independent of where the candle's UTC second lands.
 */
export interface SessionLineCfg {
  mode?: 'default' | 'deltaExpiry' | 'nseWeekly' | 'nseMonthly';
  /** 0=Sun … 6=Sat. Default 2 (Tuesday). NSE weekly/monthly. */
  nseExpiryDow?: number;
  /** IST 'HH:MM' for NSE weekly/monthly expiry. Default '15:30'. */
  nseExpiryTime?: string;
  /** IST 'HH:MM' for Delta daily option expiry. Default '17:30'. */
  deltaExpiryTime?: string;
}

/** Parse 'HH:MM' (IST) → {h,m}, with a fallback for malformed input. */
function parseHHMM(s: string | undefined, fH: number, fM: number): { h: number; m: number } {
  if (s && /^\d{1,2}:\d{2}$/.test(s)) {
    const [h, m] = s.split(':').map(Number);
    if (h >= 0 && h < 24 && m >= 0 && m < 60) return { h, m };
  }
  return { h: fH, m: fM };
}

/**
 * Option-expiry session lines. Returns the candle times of the bars that
 * CONTAIN each expiry instant, robust across timeframes (the expiry at e.g.
 * Tue 15:30 sits inside the Tuesday bar whether the chart is 1m or 1D).
 *
 * Candle times are IST-shifted UNIX seconds (read via getUTC*), so an IST
 * wall-clock instant on (y,mo,d) at (h,m) is `Date.UTC(y,mo,d,h,m)/1000`.
 *
 * Modes:
 *  - deltaExpiry: Delta India daily option expiry — every day at deltaExpiryTime
 *    IST, including weekends (daily crypto options expire 7 days/week).
 *  - nseWeekly  : weekly expiry — each nseExpiryDow at nseExpiryTime IST.
 *  - nseMonthly : monthly expiry — the LAST nseExpiryDow of each month.
 */
function sessionStartsExpiry(candles: CandleRow[], mode: 'deltaExpiry' | 'nseWeekly' | 'nseMonthly', cfg?: SessionLineCfg): number[] {
  const times = candles.map(c => c.time);
  const minT = times[0];
  const maxT = times[times.length - 1];
  const dow = cfg?.nseExpiryDow ?? 2;
  const nseT = parseHHMM(cfg?.nseExpiryTime, 15, 30);
  const deltaT = parseHHMM(cfg?.deltaExpiryTime, 17, 30);

  // Build the list of expiry instants (IST-shifted seconds) across the day span.
  const first = new Date(minT * 1000);
  const last = new Date(maxT * 1000);
  const startDayMs = Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), first.getUTCDate());
  const endDayMs = Date.UTC(last.getUTCFullYear(), last.getUTCMonth(), last.getUTCDate());
  const instants: number[] = [];
  for (let dayMs = startDayMs; dayMs <= endDayMs; dayMs += 86_400_000) {
    const dt = new Date(dayMs);
    const yy = dt.getUTCFullYear(), mm = dt.getUTCMonth(), dd = dt.getUTCDate(), wd = dt.getUTCDay();
    if (mode === 'deltaExpiry') {
      instants.push(Date.UTC(yy, mm, dd, deltaT.h, deltaT.m) / 1000);
    } else if (mode === 'nseWeekly') {
      if (wd === dow) instants.push(Date.UTC(yy, mm, dd, nseT.h, nseT.m) / 1000);
    } else { // nseMonthly — last <dow> of the month
      if (wd === dow && new Date(Date.UTC(yy, mm, dd + 7)).getUTCMonth() !== mm) {
        instants.push(Date.UTC(yy, mm, dd, nseT.h, nseT.m) / 1000);
      }
    }
  }

  // Map each in-range instant to the bar that contains it (last bar ≤ instant).
  const out: number[] = [];
  let j = 0;
  for (const inst of instants) {
    if (inst < minT || inst > maxT) continue;
    while (j < times.length && times[j] <= inst) j++;
    const k = j - 1;
    if (k >= 0) {
      const t = times[k];
      if (out.length === 0 || out[out.length - 1] !== t) out.push(t);
    }
  }
  return out;
}

export function sessionStarts(candles: CandleRow[], exchange?: string, ltfMinutes?: number, cfg?: SessionLineCfg): number[] {
  if (!candles.length) return [];
  const mode = cfg?.mode || 'default';
  if (mode !== 'default') return sessionStartsExpiry(candles, mode, cfg);
  const anchor = dayStartSec(exchange);
  const ltf = typeof ltfMinutes === 'number' && ltfMinutes > 0 ? ltfMinutes : 1;

  // Map every candle to the IST calendar day it belongs to (relative to the
  // exchange anchor) so we can detect day rollovers reliably.
  type Bucket = { time: number; y: number; mo: number; d: number; dow: number };
  const buckets: Bucket[] = candles.map(c => {
    const dt = new Date((c.time - anchor) * 1000);
    return {
      time: c.time,
      y:  dt.getUTCFullYear(),
      mo: dt.getUTCMonth(),
      d:  dt.getUTCDate(),
      // 0 = Sun, 1 = Mon … 6 = Sat
      dow: dt.getUTCDay(),
    };
  });

  // Choose grouping resolution per the LTF brackets above.
  type Resolution = 'day' | 'week' | 'month';
  const resolution: Resolution =
    ltf <= 15 ? 'day' :
    ltf <= 240 ? 'week' :
    'month';

  const out: number[] = [];
  let prevKey = '';

  if (resolution === 'day') {
    // One line per session — same as before.
    for (const b of buckets) {
      const k = `${b.y}-${b.mo}-${b.d}`;
      if (k !== prevKey) { out.push(b.time); prevKey = k; }
    }
    return out;
  }

  if (resolution === 'month') {
    // First bar within each (year, month) — the 1st trading day of the month.
    for (const b of buckets) {
      const k = `${b.y}-${b.mo}`;
      if (k !== prevKey) { out.push(b.time); prevKey = k; }
    }
    return out;
  }

  // resolution === 'week' — first session of each ISO week. We already have
  // every bar's IST date, so the first time we see a calendar day that is a
  // Monday (or the first bar after a non-Monday → Monday transition) within a
  // unique week, we treat it as a week-start. For exchanges that trade on the
  // weekend (DELTA), this still fires on Monday — the user wanted Monday as
  // the canonical week anchor regardless of whether Sat/Sun are trading days.
  let prevWeekKey = '';
  for (const b of buckets) {
    // ISO week key: Thursday rule (matches periodKey's W computation above)
    const yyyy = b.y; const mm = b.mo; const dd = b.d;
    const jan1 = Date.UTC(yyyy, 0, 1) / 1000;
    const days = Math.floor((Date.UTC(yyyy, mm, dd) / 1000 - jan1) / 86400);
    const wk = Math.floor((days + ((new Date(Date.UTC(yyyy, 0, 1))).getUTCDay() || 7) - 1) / 7);
    const weekKey = `${yyyy}-W${wk}`;
    if (weekKey !== prevWeekKey) {
      out.push(b.time);
      prevWeekKey = weekKey;
    }
  }
  return out;
}
